/**
 * Ephemeral state for the **delete-confirmation** flow (ADR-034 batch 6, #6).
 *
 * A long-lived annotation should not be lost to an accidental Delete, so every
 * delete entry point (the Entity Details panel button, the palette Delete, the
 * Delete/Backspace key) routes through here: it calls :func:`requestDelete` with
 * the id, which opens the confirmation dialog
 * (:file:`@/annotations/annotation-delete-confirm-dialog.tsx`). The actual
 * delete only fires when the operator confirms.
 *
 * Session-only UI state — the annotations persist via the BFF.
 *
 * Cross-references:
 *   - ADR-034 batch 6 (#6); feature spec §8.2.
 */
import { create } from "zustand";

interface AnnotationDeleteConfirmState {
  /** The annotation pending a delete confirmation, or ``null`` (dialog closed). */
  readonly pendingDeleteId: string | null;
  /** Open the confirmation dialog for ``id`` (called by every delete affordance). */
  readonly requestDelete: (id: string) => void;
  /** Close the dialog without deleting (Cancel / dismiss / after a confirm). */
  readonly clearPendingDelete: () => void;
}

export const useAnnotationDeleteConfirmStore = create<AnnotationDeleteConfirmState>((set) => ({
  pendingDeleteId: null,
  requestDelete: (id) => {
    set({ pendingDeleteId: id });
  },
  clearPendingDelete: () => {
    set({ pendingDeleteId: null });
  },
}));
